/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest, HttpResponse, HttpHeaders } from '@angular/common/http';
import { BaseService as __BaseService } from '../base-service';
import { WebConfiguration as __Configuration } from '../web-configuration';
import { StrictHttpResponse as __StrictHttpResponse } from '../strict-http-response';
import { Observable as __Observable } from 'rxjs';
import { map as __map, filter as __filter } from 'rxjs/operators';

import { TankSecurityDocumentList } from '../models/tank-security-document-list';

/**
 * All about the tanks security documents
 */
@Injectable({
  providedIn: 'root',
})
class TankSecurityDocumentsService extends __BaseService {
  static readonly listTankSecurityDocumentsPath = '/tanks/{uid}/documents';
  static readonly createTankSecurityDocumentPath = '/tanks/{uid}/documents';
  static readonly getTankSecurityDocumentContentPath = '/tanks/{uid}/documents/{documentUid}';
  static readonly deleteTankSecurityDocumentPath = '/tanks/{uid}/documents/{documentUid}';
  static readonly readTankSecurityDocumentPath = '/tanks/{uid}/documents/{documentUid}/read';
  static readonly getTankSecurityDocumentGetContentUrlPath = '/tanks/{uid}/documents/{documentUid}/get';
  static readonly getTankSecurityDocumentUploadContentUrlPath = '/tanks/{uid}/documents/{documentUid}/put';
  static readonly getTankSecurityDocumentDeleteContentUrlPath = '/tanks/{uid}/documents/{documentUid}/delete';

  constructor(
    config: __Configuration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * List a tank's security documents
   *
   * List all the security documents associated with a tank
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  listTankSecurityDocumentsResponse(uid: string): __Observable<__StrictHttpResponse<TankSecurityDocumentList>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;

    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/tanks/${encodeURIComponent(String(uid))}/documents`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<TankSecurityDocumentList>;
      })
    );
  }
  /**
   * List a tank's security documents
   *
   * List all the security documents associated with a tank
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  listTankSecurityDocuments(uid: string): __Observable<TankSecurityDocumentList> {
    return this.listTankSecurityDocumentsResponse(uid).pipe(
      __map(_r => _r.body as TankSecurityDocumentList)
    );
  }

  /**
   * Create a tank security document
   *
   * Create a tank security document and its associated file
   * @param params The `TankSecurityDocumentsService.CreateTankSecurityDocumentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `file`: File used to create a document
   *
   * - `validityStartDate`: Validity start date used to create a document
   *
   * - `validityEndDate`: Validity end date used to create a document
   *
   * - `role`: Role used to create a tank security document:
   *     * SECURITY_PROTOCOL
   *     * UNLOADING_INSTRUCTIONS
   *     * PREVENTION_PLAN
   *     * STATION_MAP
   *     * OTHER
   */
  createTankSecurityDocumentResponse(params: TankSecurityDocumentsService.CreateTankSecurityDocumentParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;
    let __formData = new FormData();
    __body = __formData;

    if (params.file != null) { __formData.append('file', params.file as string | Blob);}
    if (params.validityStartDate != null) { __formData.append('validityStartDate', params.validityStartDate as string | Blob);}
    if (params.validityEndDate != null) { __formData.append('validityEndDate', params.validityEndDate as string | Blob);}
    if (params.role != null) { __formData.append('role', params.role as string | Blob);}
    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/tanks/${encodeURIComponent(String(params.uid))}/documents`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Create a tank security document
   *
   * Create a tank security document and its associated file
   * @param params The `TankSecurityDocumentsService.CreateTankSecurityDocumentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `file`: File used to create a document
   *
   * - `validityStartDate`: Validity start date used to create a document
   *
   * - `validityEndDate`: Validity end date used to create a document
   *
   * - `role`: Role used to create a tank security document:
   *     * SECURITY_PROTOCOL
   *     * UNLOADING_INSTRUCTIONS
   *     * PREVENTION_PLAN
   *     * STATION_MAP
   *     * OTHER
   */
  createTankSecurityDocument(params: TankSecurityDocumentsService.CreateTankSecurityDocumentParams): __Observable<null> {
    return this.createTankSecurityDocumentResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Get a tank security document's content
   *
   * Get the file content of a tank security document
   * @param params The `TankSecurityDocumentsService.GetTankSecurityDocumentContentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getTankSecurityDocumentContentResponse(params: TankSecurityDocumentsService.GetTankSecurityDocumentContentParams): __Observable<__StrictHttpResponse<Blob>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/tanks/${encodeURIComponent(String(params.uid))}/documents/${encodeURIComponent(String(params.documentUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'blob'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<Blob>;
      })
    );
  }
  /**
   * Get a tank security document's content
   *
   * Get the file content of a tank security document
   * @param params The `TankSecurityDocumentsService.GetTankSecurityDocumentContentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getTankSecurityDocumentContent(params: TankSecurityDocumentsService.GetTankSecurityDocumentContentParams): __Observable<Blob> {
    return this.getTankSecurityDocumentContentResponse(params).pipe(
      __map(_r => _r.body as Blob)
    );
  }

  /**
   * Delete a tank security document and its content
   *
   * Delete a tank security document and its associated file
   * @param params The `TankSecurityDocumentsService.DeleteTankSecurityDocumentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   */
  deleteTankSecurityDocumentResponse(params: TankSecurityDocumentsService.DeleteTankSecurityDocumentParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'DELETE',
      this.rootUrl + `/tanks/${encodeURIComponent(String(params.uid))}/documents/${encodeURIComponent(String(params.documentUid))}`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Delete a tank security document and its content
   *
   * Delete a tank security document and its associated file
   * @param params The `TankSecurityDocumentsService.DeleteTankSecurityDocumentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   */
  deleteTankSecurityDocument(params: TankSecurityDocumentsService.DeleteTankSecurityDocumentParams): __Observable<null> {
    return this.deleteTankSecurityDocumentResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Read a tank security document
   *
   * Marks the tank security document as read by the user making the request
   * @param params The `TankSecurityDocumentsService.ReadTankSecurityDocumentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   */
  readTankSecurityDocumentResponse(params: TankSecurityDocumentsService.ReadTankSecurityDocumentParams): __Observable<__StrictHttpResponse<null>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'POST',
      this.rootUrl + `/tanks/${encodeURIComponent(String(params.uid))}/documents/${encodeURIComponent(String(params.documentUid))}/read`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'json'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<null>;
      })
    );
  }
  /**
   * Read a tank security document
   *
   * Marks the tank security document as read by the user making the request
   * @param params The `TankSecurityDocumentsService.ReadTankSecurityDocumentParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   */
  readTankSecurityDocument(params: TankSecurityDocumentsService.ReadTankSecurityDocumentParams): __Observable<null> {
    return this.readTankSecurityDocumentResponse(params).pipe(
      __map(_r => _r.body as null)
    );
  }

  /**
   * Get a tank security document's get content pre-signed URL
   *
   * Get a pre-signed URL to fetch the tank security document's content directly from RustFS
   * @param params The `TankSecurityDocumentsService.GetTankSecurityDocumentGetContentUrlParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getTankSecurityDocumentGetContentUrlResponse(params: TankSecurityDocumentsService.GetTankSecurityDocumentGetContentUrlParams): __Observable<__StrictHttpResponse<string>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/tanks/${encodeURIComponent(String(params.uid))}/documents/${encodeURIComponent(String(params.documentUid))}/get`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'text'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<string>;
      })
    );
  }
  /**
   * Get a tank security document's get content pre-signed URL
   *
   * Get a pre-signed URL to fetch the tank security document's content directly from RustFS
   * @param params The `TankSecurityDocumentsService.GetTankSecurityDocumentGetContentUrlParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getTankSecurityDocumentGetContentUrl(params: TankSecurityDocumentsService.GetTankSecurityDocumentGetContentUrlParams): __Observable<string> {
    return this.getTankSecurityDocumentGetContentUrlResponse(params).pipe(
      __map(_r => _r.body as string)
    );
  }

  /**
   * Get a tank security document's upload content pre-signed URL
   *
   * Get a pre-signed URL to upload the tank security document's content directly to RustFS
   * @param params The `TankSecurityDocumentsService.GetTankSecurityDocumentUploadContentUrlParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getTankSecurityDocumentUploadContentUrlResponse(params: TankSecurityDocumentsService.GetTankSecurityDocumentUploadContentUrlParams): __Observable<__StrictHttpResponse<string>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/tanks/${encodeURIComponent(String(params.uid))}/documents/${encodeURIComponent(String(params.documentUid))}/put`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'text'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<string>;
      })
    );
  }
  /**
   * Get a tank security document's upload content pre-signed URL
   *
   * Get a pre-signed URL to upload the tank security document's content directly to RustFS
   * @param params The `TankSecurityDocumentsService.GetTankSecurityDocumentUploadContentUrlParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getTankSecurityDocumentUploadContentUrl(params: TankSecurityDocumentsService.GetTankSecurityDocumentUploadContentUrlParams): __Observable<string> {
    return this.getTankSecurityDocumentUploadContentUrlResponse(params).pipe(
      __map(_r => _r.body as string)
    );
  }

  /**
   * Get a tank security document's delete content pre-signed URL
   *
   * Get a pre-signed URL to delete the tank security document's content directly from RustFS
   * @param params The `TankSecurityDocumentsService.GetTankSecurityDocumentDeleteContentUrlParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getTankSecurityDocumentDeleteContentUrlResponse(params: TankSecurityDocumentsService.GetTankSecurityDocumentDeleteContentUrlParams): __Observable<__StrictHttpResponse<string>> {
    let __params = this.newParams();
    let __headers = new HttpHeaders();
    let __body: any = null;


    let req = new HttpRequest<any>(
      'GET',
      this.rootUrl + `/tanks/${encodeURIComponent(String(params.uid))}/documents/${encodeURIComponent(String(params.documentUid))}/delete`,
      __body,
      {
        headers: __headers,
        params: __params,
        responseType: 'text'
      });

    return this.http.request<any>(req).pipe(
      __filter(_r => _r instanceof HttpResponse),
      __map((_r) => {
        return _r as __StrictHttpResponse<string>;
      })
    );
  }
  /**
   * Get a tank security document's delete content pre-signed URL
   *
   * Get a pre-signed URL to delete the tank security document's content directly from RustFS
   * @param params The `TankSecurityDocumentsService.GetTankSecurityDocumentDeleteContentUrlParams` containing the following parameters:
   *
   * - `uid`: The object ID. Must be a valid ID.
   *
   * - `documentUid`: The object ID. Must be a valid ID.
   *
   * @return Success
   */
  getTankSecurityDocumentDeleteContentUrl(params: TankSecurityDocumentsService.GetTankSecurityDocumentDeleteContentUrlParams): __Observable<string> {
    return this.getTankSecurityDocumentDeleteContentUrlResponse(params).pipe(
      __map(_r => _r.body as string)
    );
  }
}

namespace TankSecurityDocumentsService {

  /**
   * Parameters for createTankSecurityDocument
   */
  export interface CreateTankSecurityDocumentParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * File used to create a document
     */
    file: Blob;

    /**
     * Validity start date used to create a document
     */
    validityStartDate?: string;

    /**
     * Validity end date used to create a document
     */
    validityEndDate?: string;

    /**
     * Role used to create a tank security document:
     *   * SECURITY_PROTOCOL
     *   * UNLOADING_INSTRUCTIONS
     *   * PREVENTION_PLAN
     *   * STATION_MAP
     *   * OTHER
     */
    role?: string;
  }

  /**
   * Parameters for getTankSecurityDocumentContent
   */
  export interface GetTankSecurityDocumentContentParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    documentUid: string;
  }

  /**
   * Parameters for deleteTankSecurityDocument
   */
  export interface DeleteTankSecurityDocumentParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    documentUid: string;
  }

  /**
   * Parameters for readTankSecurityDocument
   */
  export interface ReadTankSecurityDocumentParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    documentUid: string;
  }

  /**
   * Parameters for getTankSecurityDocumentGetContentUrl
   */
  export interface GetTankSecurityDocumentGetContentUrlParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    documentUid: string;
  }

  /**
   * Parameters for getTankSecurityDocumentUploadContentUrl
   */
  export interface GetTankSecurityDocumentUploadContentUrlParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    documentUid: string;
  }

  /**
   * Parameters for getTankSecurityDocumentDeleteContentUrl
   */
  export interface GetTankSecurityDocumentDeleteContentUrlParams {

    /**
     * The object ID. Must be a valid ID.
     */
    uid: string;

    /**
     * The object ID. Must be a valid ID.
     */
    documentUid: string;
  }
}

export { TankSecurityDocumentsService }
